import type { Locale } from "@/i18n-config";
import type { Dictionary } from "@/dictionaries/types";

import { NAV_CATALOGUE, NAV_GROUP_ORDER } from "./catalogue";
import { isProtectedPath } from "./protected-routes";
import type {
  LocalizedNavItem,
  NavAudience,
  NavAudienceResolved,
  NavGroup,
  NavItem,
  NavViewerState,
} from "./types";

export { NAV_CATALOGUE, NAV_GROUP_ORDER } from "./catalogue";
export {
  resolveShellMode,
  shellShowsPublicFooter,
  routeFamilySegment,
  WORKSPACE_RAIL_INSET_VAR,
  type ShellMode,
} from "./shell-routes";
export * from "./types";
export {
  isProtectedPath,
  isAuthOnlyPath,
  PROTECTED_ROUTE_PREFIXES,
  PROTECTED_ROUTE_PATTERNS,
  AUTH_ROUTES,
} from "./protected-routes";

/* =========================================================================
   Q1 — guest visibility strategy
   ========================================================================= */

/**
 * Two coherent strategies exist and the product has not chosen between them:
 *
 *   "hide" — a guest never sees a protected destination. What ships today.
 *   "gate" — a guest sees the destination, and clicking it opens the login
 *            modal. What `useLikeToggle` / `useWatchlistToggle` already do,
 *            so the app contains both philosophies.
 *
 * Q1 is unresolved and Phase 01 is explicitly forbidden from resolving it
 * without product evidence. It is also forbidden from letting that block the
 * structural work — hence one switchable constant rather than the rule being
 * re-implemented in each component.
 *
 * INTERIM RULING: "hide", which preserves current behaviour byte for byte.
 *
 * REVERSAL COST: flip this constant. `resolveNavAudience` then marks protected
 * items `gated` instead of filtering them, and the surfaces already render
 * that state. No component changes, no catalogue changes.
 */
export type GuestVisibilityStrategy = "hide" | "gate";
export const GUEST_VISIBILITY_STRATEGY: GuestVisibilityStrategy = "hide";

/* =========================================================================
   Audience derivation — from the middleware, never by hand
   ========================================================================= */

/**
 * A catalogue entry's audience is a FUNCTION of the middleware's protected
 * list. It is not stored, so it cannot drift from `proxy.ts`.
 */
export function audienceOf(item: NavItem): NavAudience {
  return isProtectedPath(item.path) ? "auth" : "public";
}

/**
 * Resolve an entry against the viewer, or `null` when it must not render.
 */
export function resolveNavAudience(
  item: NavItem,
  viewer: NavViewerState
): NavAudienceResolved | null {
  const audience = audienceOf(item);

  if (audience === "auth" && !viewer.isAuthenticated) {
    if (GUEST_VISIBILITY_STRATEGY === "hide") return null;
    return { ...item, audience, gated: true };
  }

  if (audience === "guest" && viewer.isAuthenticated) return null;

  return { ...item, audience, gated: false };
}

/* =========================================================================
   Path normalisation and active matching
   ========================================================================= */

/** Strip the leading `/en` or `/fa` segment. `/en` and `/en/` both give `/`. */
export function stripLocale(pathname: string, lang: Locale): string {
  const prefix = `/${lang}`;
  if (pathname === prefix) return "/";
  if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length) || "/";
  return pathname || "/";
}

/**
 * Drop query and hash, and the trailing slash (except on root), so
 * `/market?tab=all`, `/market/` and `/market` are one path.
 *
 * `usePathname()` already excludes the query string, but callers also pass
 * raw hrefs (`/settings#blue-tick`), so both are handled here rather than
 * being assumed away.
 */
export function normalizePath(path: string): string {
  const withoutQuery = path.split(/[?#]/)[0] || "/";
  if (withoutQuery.length > 1 && withoutQuery.endsWith("/")) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery || "/";
}

/**
 * How specifically this entry matches, or -1 for no match.
 *
 * The return value is the length of the matched base path, which lets the
 * caller pick the single most specific entry instead of lighting up every
 * ancestor. Broad prefix matching is what produced false active states
 * before: `startsWith("/auctions")` marked "my auctions" current while the
 * user was reading a public auction detail page.
 */
export function matchDepth(item: NavItem, pathWithoutLocale: string): number {
  const path = normalizePath(pathWithoutLocale);

  const testBase = (base: string, mode: NavItem["match"]): number => {
    const normalizedBase = normalizePath(base);
    if (path === normalizedBase) return normalizedBase.length;
    if (mode === "exact") return -1;
    // Root is a family of everything, which would make it always-active.
    if (normalizedBase === "/") return -1;
    if (path.startsWith(`${normalizedBase}/`)) return normalizedBase.length;
    return -1;
  };

  let best = testBase(item.path, item.match);

  for (const extra of item.familyPaths ?? []) {
    // Cross-family mappings are always family-matched: `/assets` stands for
    // every NFT detail beneath it.
    best = Math.max(best, testBase(extra, "family"));
  }

  return best;
}

/**
 * The id of the single entry that should carry `aria-current="page"`, or null.
 *
 * Returning one id — rather than each item deciding for itself — is what
 * guarantees exactly one active entry per surface, including when a nested
 * route matches both a parent and a child entry.
 */
export function resolveActiveId(
  items: readonly NavItem[],
  pathname: string | null | undefined,
  lang: Locale
): string | null {
  if (!pathname) return null;
  const path = stripLocale(normalizePath(pathname), lang);

  let bestId: string | null = null;
  let bestDepth = -1;
  let bestIsExact = false;

  for (const item of items) {
    const depth = matchDepth(item, path);
    if (depth < 0) continue;

    // AN EXACT MATCH OUTRANKS A FAMILY MATCH OF THE SAME LENGTH, and this tie
    // is real rather than hypothetical: `/auctions` is matched by `myAuctions`
    // (the signed-in index, `match: "exact"`) AND, since it was given
    // `familyPaths: ["/auctions"]` so that public auction DETAIL pages resolve
    // to the public auctions listing, by `activeBid` too. Both report depth 9.
    // Without this the winner would be decided by declaration order in the
    // catalogue — which is presentation order, not specificity — and reordering
    // the file would silently move the active state.
    const isExact = item.match === "exact";
    const wins = depth > bestDepth || (depth === bestDepth && isExact && !bestIsExact);
    if (wins) {
      bestDepth = depth;
      bestIsExact = isExact;
      bestId = item.id;
    }
  }

  return bestDepth >= 0 ? bestId : null;
}

/* =========================================================================
   Localisation
   ========================================================================= */

export function hrefFor(item: NavItem, lang: Locale): string {
  return item.path === "/" ? `/${lang}` : `/${lang}${item.path}`;
}

export function localizeNavItem(
  item: NavAudienceResolved,
  lang: Locale,
  dict: Dictionary
): LocalizedNavItem {
  return {
    ...item,
    lang,
    href: hrefFor(item, lang),
    label: dict.nav[item.id],
  };
}

/* =========================================================================
   Surface selectors — every consumer derives from these
   ========================================================================= */

type SurfaceKey = "desktop" | "mobileDrawer" | "account" | "footer";

/**
 * True when a signed-in viewer would see this entry as a duplicate of another
 * on this surface. Declared in the catalogue, applied here, so every surface
 * derives the rule instead of each component re-checking a pathname.
 */
function isSupersededHere(item: NavItem, surface: SurfaceKey, viewer: NavViewerState): boolean {
  const rule = item.supersededWhenAuthenticated;
  return Boolean(rule && viewer.isAuthenticated && rule.on.includes(surface));
}

function selectSurface(
  surface: SurfaceKey,
  viewer: NavViewerState,
  lang: Locale,
  dict: Dictionary
): LocalizedNavItem[] {
  return NAV_CATALOGUE.filter((item) => item.surfaces[surface])
    .filter((item) => !isSupersededHere(item, surface, viewer))
    .map((item) => resolveNavAudience(item, viewer))
    .filter((item): item is NavAudienceResolved => item !== null)
    .map((item) => localizeNavItem(item, lang, dict));
}

export function getDesktopNav(viewer: NavViewerState, lang: Locale, dict: Dictionary) {
  return selectSurface("desktop", viewer, lang, dict);
}

export function getMobileDrawerNav(viewer: NavViewerState, lang: Locale, dict: Dictionary) {
  return selectSurface("mobileDrawer", viewer, lang, dict);
}

export function getFooterNav(viewer: NavViewerState, lang: Locale, dict: Dictionary) {
  return selectSurface("footer", viewer, lang, dict);
}

/**
 * The mobile tab row, honouring the guest/auth branching recorded in the
 * catalogue. The row (including the Menu trigger, and the create FAB for
 * auth) must render at most 5 items — Galleries (guest) was dropped from this
 * surface for that reason and stays reachable via the Quick Menu drawer.
 * Current composition:
 *
 *   guest → Home · Market · Search · Artists · Menu
 *   auth  → Dashboard · Wallet · [create FAB] · Search · Menu
 *
 * Slot 1 is Home for a guest and Dashboard for a signed-in viewer — the same
 * position, pointing at the canonical URL for that audience. It is not two
 * entries; `home` and `dashboard` each declare `showFor` on this surface so
 * exactly one of them is ever selected. Slot 2 works the same way: Market
 * declares `showFor: "guest"` and Wallet declares `showFor: "auth"` on the
 * same order, so exactly one of them fills it per viewer — Market stays
 * reachable for a signed-in phone user via the sidebar, dashboard and drawer.
 */
export function getMobileTabs(
  viewer: NavViewerState,
  lang: Locale,
  dict: Dictionary
): LocalizedNavItem[] {
  const audienceKey = viewer.isAuthenticated ? "auth" : "guest";

  return NAV_CATALOGUE.filter((item) => {
    const tab = item.surfaces.mobileTab;
    if (!tab) return false;
    return tab.showFor === "all" || tab.showFor === audienceKey;
  })
    .map((item) => resolveNavAudience(item, viewer))
    .filter((item): item is NavAudienceResolved => item !== null)
    .sort((a, b) => (a.surfaces.mobileTab!.order ?? 0) - (b.surfaces.mobileTab!.order ?? 0))
    .map((item) => localizeNavItem(item, lang, dict));
}

/** Group a surface's items, in the shared group order, dropping empty groups. */
export function groupNavItems(
  items: readonly LocalizedNavItem[]
): Array<{ group: NavGroup; items: LocalizedNavItem[] }> {
  return NAV_GROUP_ORDER.map((group) => ({
    group,
    items: items.filter((item) => item.group === group),
  })).filter((section) => section.items.length > 0);
}
